<template>
  <div class="organization-management">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <h2>{{ $t('organization.title') }}</h2>
        <div class="header-actions">
          <a-space>
            <a-button type="primary" @click="showCreateModal">
              <template #icon><PlusOutlined /></template>
              {{ $t('organization.createUserGroup') }}
            </a-button>
          </a-space>
        </div>
      </div>
    </div>

    <div class="content-wrapper">
      <a-row :gutter="24">
        <!-- 左侧：组织架构树 -->
        <a-col :span="12">
          <a-card :title="$t('organization.organizationTree')" class="tree-card">
            <template #extra>
              <a-space>
                <a-input-search
                  v-model:value="searchValue"
                  :placeholder="$t('organization.searchUserGroup')"
                  style="width: 200px"
                  @search="onSearch"
                />
                <a-tooltip :title="$t('common.refresh')">
                  <a-button type="text" @click="refreshTree">
                    <template #icon><ReloadOutlined /></template>
                  </a-button>
                </a-tooltip>
              </a-space>
            </template>

            <a-spin :spinning="treeLoading">
              <a-tree
                v-model:expandedKeys="expandedKeys"
                v-model:selectedKeys="selectedKeys"
                :tree-data="treeData"
                :field-names="{ children: 'children', title: 'title', key: 'key' }"
                draggable
                block-node
                show-line
                @select="onSelectNode"
                @drop="onDrop"
                @right-click="onRightClick"
              >
                <template #title="{ title, group_type, status, user_count }">
                  <div class="tree-node-title">
                    <span class="node-name">{{ title }}</span>
                    <a-space class="node-tags">
                      <a-tag 
                        :color="group_type === 'metalingual' ? 'purple' : 'blue'" 
                        size="small"
                      >
                        {{ getGroupTypeLabel(group_type) }}
                      </a-tag>
                      <a-tag 
                        :color="status === 'active' ? 'green' : 'red'" 
                        size="small"
                      >
                        {{ getStatusText(status) }}
                      </a-tag>
                      <a-tag color="orange" size="small">
                        {{ $t('organization.people', { count: user_count || 0 }) }}
                      </a-tag>
                    </a-space>
                  </div>
                </template>
              </a-tree>
            </a-spin>

            <!-- 右键菜单 -->
            <a-dropdown
              v-model:open="contextMenuVisible"
              :trigger="['contextmenu']"
              placement="bottomLeft"
            >
              <div></div>
              <template #overlay>
                <a-menu @click="handleContextMenu">
                  <a-menu-item key="edit">
                    <EditOutlined />
                    {{ $t('organization.context.edit') }}
                  </a-menu-item>
                  <a-menu-item key="addChild">
                    <PlusOutlined />
                    {{ $t('organization.context.addChild') }}
                  </a-menu-item>
                  <a-menu-divider />
                  <a-menu-item key="delete" danger>
                    <DeleteOutlined />
                    {{ $t('organization.context.delete') }}
                  </a-menu-item>
                </a-menu>
              </template>
            </a-dropdown>
          </a-card>
        </a-col>

        <!-- 右侧：用户组详情 -->
        <a-col :span="12">
          <a-card :title="$t('organization.userGroupDetails')" class="detail-card">
            <template #extra>
              <a-space v-if="selectedGroup">
                <a-button type="primary" @click="editGroup">
                  <template #icon><EditOutlined /></template>
                  {{ $t('common.edit') }}
                </a-button>
                <a-button danger @click="deleteGroup">
                  <template #icon><DeleteOutlined /></template>
                  {{ $t('common.delete') }}
                </a-button>
              </a-space>
            </template>

            <div v-if="!selectedGroup" class="empty-state">
              <a-empty :description="$t('organization.selectGroupMessage')" />
            </div>

            <div v-else class="group-detail">
              <!-- 基本信息 -->
              <a-descriptions :title="$t('organization.basicInfo')" :column="1" bordered>
                <a-descriptions-item :label="$t('organization.groupName')">
                  {{ selectedGroup.name }}
                </a-descriptions-item>
                <a-descriptions-item :label="$t('organization.groupType')">
                  <a-tag :color="selectedGroup.group_type === 'metalingual' ? 'purple' : 'blue'">
                    {{ getGroupTypeDetailLabel(selectedGroup.group_type) }}
                  </a-tag>
                </a-descriptions-item>
                <a-descriptions-item :label="$t('common.status')">
                  <a-tag :color="selectedGroup.status === 'active' ? 'green' : 'red'">
                    {{ getStatusText(selectedGroup.status) }}
                  </a-tag>
                </a-descriptions-item>
                <a-descriptions-item :label="$t('organization.description')">
                  {{ selectedGroup.description || $t('organization.noDescription') }}
                </a-descriptions-item>
                <a-descriptions-item :label="$t('organization.createTime')">
                  {{ formatDateTime(selectedGroup.created_at) }}
                </a-descriptions-item>
                <a-descriptions-item :label="$t('organization.updateTime')">
                  {{ formatDateTime(selectedGroup.updated_at) }}
                </a-descriptions-item>
              </a-descriptions>

              
              <!-- 子组说明 -->
              <div v-if="selectedGroup.parent_id" class="subgroup-info">
                <a-alert
                  :message="$t('organization.subgroupInfo')"
                  :description="$t('organization.subgroupMessage')"
                  type="info"
                  show-icon
                />
              </div>

              <!-- 成员列表 -->
              <a-divider>{{ $t('organization.memberList', { count: groupUsers.length }) }}</a-divider>
              <a-spin :spinning="usersLoading">
                <a-list
                  :data-source="groupUsers"
                  :pagination="usersPagination"
                  size="small"
                >
                  <template #renderItem="{ item }">
                    <a-list-item>
                      <a-list-item-meta>
                        <template #title>
                          <a-space>
                            <span>{{ item.name }}</span>
                            <template v-if="item.roles?.length">
                              <a-tag v-for="r in item.roles" :key="r.id" size="small">{{ r.name }}</a-tag>
                            </template>
                          </a-space>
                        </template>
                        <template #description>
                          <div>
                            <div>{{ $t('organization.username') }}: {{ item.username }}</div>
                            <div>{{ $t('organization.email') }}: {{ item.email }}</div>
                            <div v-if="item.language">{{ $t('organization.language') }}: {{ item.language }}</div>
                          </div>
                        </template>
                      </a-list-item-meta>
                      <template #actions>
                        <a @click="removeFromGroup(item)" style="color: #ff4d4f">{{ $t('organization.remove') }}</a>
                      </template>
                    </a-list-item>
                  </template>
                </a-list>
              </a-spin>
            </div>
          </a-card>
        </a-col>
      </a-row>
    </div>

    <!-- 创建/编辑用户组模态框 -->
    <a-modal
      v-model:open="modalVisible"
      :title="isEditing ? $t('organization.editTitle') : $t('organization.createTitle')"
      :confirm-loading="modalLoading"
      @ok="handleSubmit"
      @cancel="handleCancel"
    >
      <a-form
        ref="formRef"
        :model="formData"
        :rules="formRules"
        layout="vertical"
      >
        <a-form-item :label="$t('organization.groupName')" name="name">
          <a-input v-model:value="formData.name" :placeholder="$t('organization.enterGroupName')" />
        </a-form-item>

        <a-form-item :label="$t('organization.groupType')" name="group_type">
          <a-select v-model:value="formData.group_type" :placeholder="$t('organization.selectGroupType')">
            <a-select-option value="normal">{{ $t('organization.normalGroup') }}</a-select-option>
            <a-select-option value="metalingual">{{ $t('organization.metalingualGroup') }}</a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item :label="$t('organization.parentGroup')" name="parent_id">
          <a-tree-select
            v-model:value="formData.parent_id"
            :tree-data="parentGroupOptions"
            :field-names="{ children: 'children', title: 'title', key: 'value' }"
            :placeholder="$t('organization.selectParentGroup')"
            allow-clear
            tree-default-expand-all
          />
        </a-form-item>

        <a-form-item :label="$t('common.status')" name="status" v-if="isEditing">
          <a-select v-model:value="formData.status" :placeholder="$t('organization.selectStatus')">
            <a-select-option value="active">{{ $t('organization.status.active') }}</a-select-option>
            <a-select-option value="inactive">{{ $t('organization.status.inactive') }}</a-select-option>
            <a-select-option value="suspended">{{ $t('organization.status.suspended') }}</a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item :label="$t('organization.description')" name="description">
          <a-textarea 
            v-model:value="formData.description" 
            :placeholder="$t('organization.enterDescription')"
            :rows="3"
          />
        </a-form-item>

        <a-form-item :label="$t('organization.platformName')" name="platform_name">
          <a-input 
            v-model:value="formData.platform_name" 
            :placeholder="$t('organization.enterPlatformName')"
            :maxlength="50"
            show-count
          />
        </a-form-item>

        <a-form-item label="Logo">
          <a-upload
            :file-list="logoFileList"
            :before-upload="beforeUploadLogo"
            :remove="handleRemoveLogo"
            accept="image/*"
            list-type="picture-card"
            :max-count="1"
          >
            <div v-if="logoFileList.length < 1">
              <PlusOutlined />
              <div style="margin-top: 8px">{{ $t('organization.uploadLogo') }}</div>
            </div>
          </a-upload>
          <div v-if="logoPreviewUrl" style="margin-top: 8px">
            <img :src="logoPreviewUrl" :alt="$t('organization.currentLogo')" style="width: 100px; height: 100px; object-fit: contain; border: 1px solid #d9d9d9; border-radius: 4px;" />
            <div style="margin-top: 4px; color: #666; font-size: 12px;">{{ $t('organization.currentLogo') }}</div>
          </div>
          <div style="margin-top: 8px; color: #999; font-size: 12px;">
            <div>{{ $t('organization.logoFormats') }}</div>
            <div style="margin-top: 4px;">{{ $t('organization.logoSize') }}</div>
          </div>
        </a-form-item>
        
        <!-- 子组提示信息 -->
        <a-form-item v-if="formData.parent_id">
          <a-alert
            :message="$t('organization.subgroupNote')"
            :description="$t('organization.subgroupNoteMessage')"
            type="info"
            show-icon
          />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, computed, watch, nextTick } from 'vue'
import { message, Modal } from 'ant-design-vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined
} from '@ant-design/icons-vue'
import { userApi, type UserGroup, type UserInfo } from '@/modules/user/api'
import { useUserStore } from '@/stores'
import { getCorrectedApiOrigin } from '@/shared/api/api-base'

// 路由
const router = useRouter()
const userStore = useUserStore()

const { t } = useI18n()

// 响应式数据
const treeLoading = ref(false)
const usersLoading = ref(false)
const modalVisible = ref(false)
const modalLoading = ref(false)
const contextMenuVisible = ref(false)
const isEditing = ref(false)
const searchValue = ref('')

// 树形数据
const treeData = ref<any[]>([])
const expandedKeys = ref<string[]>([])
const selectedKeys = ref<string[]>([])
const selectedGroup = ref<UserGroup | null>(null)
const contextMenuNode = ref<any>(null)

// 用户列表
const groupUsers = ref<UserInfo[]>([])
const usersPagination = reactive({
  current: 1,
  pageSize: 10,
  total: 0,
  showSizeChanger: true,
  showQuickJumper: true
})

// 表单数据
const formRef = ref()
const formData = reactive({
  name: '',
  description: '',
  group_type: 'normal' as 'normal' | 'metalingual',
  parent_id: undefined as number | undefined,
  status: 'active' as 'active' | 'inactive' | 'suspended',
  platform_name: 'CollabLingua' as string  // 默认值
})

// Logo上传相关
const logoFileList = ref<any[]>([])
const logoFile = ref<File | null>(null)

// Logo预览URL（走用户组 logo API，与 MainLayout 一致）
const logoPreviewUrl = computed(() => {
  if (selectedGroup.value?.id) {
    const baseURL = getCorrectedApiOrigin() || ''
    if (baseURL) {
      return `${baseURL}/api/v1/users/groups/${selectedGroup.value.id}/logo`
    }
    return `/api/v1/users/groups/${selectedGroup.value.id}/logo`
  }
  return null
})

// 表单验证规则
const formRules = computed(() => ({
  name: [
    { required: true, message: t('organization.groupNameRequired'), trigger: 'blur' },
    { min: 1, max: 100, message: t('organization.groupNameLength'), trigger: 'blur' }
  ],
  group_type: [
    { required: true, message: t('organization.groupTypeRequired'), trigger: 'change' }
  ],
  platform_name: [
    { max: 50, message: t('organization.platformNameMaxLength'), trigger: 'blur' }
  ]
}))

// 计算属性
const filteredTreeData = computed(() => {
  if (!searchValue.value) return treeData.value
  return filterTreeData(treeData.value, searchValue.value)
})

const parentGroupOptions = computed(() => {
  const buildOptions = (groups: any[], excludeId?: number): any[] => {
    return groups
      .filter(group => group.id !== excludeId) // 排除当前编辑的组
      .map(group => ({
        title: group.title,
        value: group.id,
        children: group.children ? buildOptions(group.children, excludeId) : []
      }))
  }
  
  const excludeId = isEditing.value && selectedGroup.value ? selectedGroup.value.id : undefined
  return buildOptions(treeData.value, excludeId)
})

// 生命周期
onMounted(() => {
  console.log('[organization] component mounted, loading data...')
  loadTreeData()
})

// 监听选中节点变化
watch(selectedKeys, (newKeys) => {
  if (newKeys.length > 0) {
    const groupId = parseInt(newKeys[0])
    // 确保groupId是有效的正数
    if (!isNaN(groupId) && groupId > 0) {
      loadGroupDetail(groupId)
    } else {
      console.warn('Invalid group ID:', newKeys[0])
      selectedKeys.value = []
      selectedGroup.value = null
      groupUsers.value = []
    }
  } else {
    selectedGroup.value = null
    groupUsers.value = []
  }
})

// 方法定义
const loadTreeData = async () => {
  try {
    treeLoading.value = true
    console.log('[organization] start loading tree data...')
    const groups = await userApi.getUserGroupTree()
    console.log('[organization] raw groups response:', groups)
    console.log('[organization] group count:', groups.length)
    treeData.value = buildTreeData(groups)
    console.log('[organization] processed tree data:', treeData.value)
    
    // 默认展开第一级，确保ID有效
    expandedKeys.value = groups
      .filter(g => !g.parent_id && g.id && g.id > 0)
      .map(g => g.id.toString())
      .filter(key => key && key !== '0' && key !== 'NaN')
  } catch (error) {
    console.error('[organization] failed to load tree data:', error)
  } finally {
    treeLoading.value = false
  }
}

const buildTreeData = (groups: UserGroup[]): any[] => {
  const groupMap = new Map()
  const rootGroups: any[] = []

  console.log('[organization] building tree data, input groups:', groups)
  console.log('[organization] input group count:', groups.length)

  // 创建节点映射
  groups.forEach((group, index) => {
    console.log(`🔍 处理组 ${index + 1}/${groups.length}:`, {
      id: group.id,
      name: group.name,
      nameLength: group.name ? group.name.length : 0,
      parent_id: group.parent_id
    })
    
    // 确保组有有效的ID和名称
    if (!group.id || !group.name || group.name.trim() === '') {
      console.warn('[organization] skip invalid group:', {
        id: group.id,
        name: group.name,
        nameTrimmed: group.name ? group.name.trim() : 'null'
      })
      return
    }
    
    const node = {
      key: group.id.toString(),
      user_count: 0, // 这里可以从API获取用户数量
      children: [],
      ...group,
      title: group.name // 确保title使用name，覆盖可能的空title
    }
    
    // 调试：检查group对象是否包含title属性
    if ((group as any).hasOwnProperty('title')) {
      console.warn(`[organization] group ${group.id} contains title property:`, {
        groupTitle: (group as any).title,
        groupName: group.name,
        finalTitle: node.title
      })
    }
    
    console.log(`[organization] create tree node for group ${group.id}:`, {
      key: node.key,
      title: node.title,
      name: node.name,
      parent_id: node.parent_id
    })
    groupMap.set(group.id, node)
  })

  console.log('[organization] node map created, size:', groupMap.size)

  // 构建树形结构
  groups.forEach(group => {
    // 再次检查组的有效性
    if (!group.id || !group.name || group.name.trim() === '') {
      return
    }
    
    const node = groupMap.get(group.id)
    if (node) {
      if (group.parent_id && groupMap.has(group.parent_id)) {
        console.log(`[organization] append child node: ${group.name} -> parent ${group.parent_id}`)
        groupMap.get(group.parent_id).children.push(node)
      } else {
        console.log(`[organization] append root node: ${group.name}`)
        rootGroups.push(node)
      }
    }
  })

  console.log('[organization] final root count:', rootGroups.length)
  console.log('[organization] final tree data:', rootGroups)
  
  // 检查是否有空title
  const checkEmptyTitles = (nodes: any[]): any[] => {
    const emptyTitles: any[] = []
    nodes.forEach(node => {
      if (!node.title || node.title.trim() === '') {
        emptyTitles.push(node)
        console.error('[organization] empty title node found:', node)
      }
      if (node.children && node.children.length > 0) {
        emptyTitles.push(...checkEmptyTitles(node.children))
      }
    })
    return emptyTitles
  }
  
  const emptyTitles = checkEmptyTitles(rootGroups)
  if (emptyTitles.length > 0) {
    console.error('[organization] empty title nodes count:', emptyTitles.length)
  } else {
    console.log('[organization] no empty title nodes')
  }

  return rootGroups
}

const filterTreeData = (data: any[], keyword: string): any[] => {
  return data.filter(item => {
    const matchesKeyword = item.title.toLowerCase().includes(keyword.toLowerCase())
    const hasMatchingChildren = item.children && item.children.length > 0 && 
      filterTreeData(item.children, keyword).length > 0
    
    if (hasMatchingChildren) {
      item.children = filterTreeData(item.children, keyword)
    }
    
    return matchesKeyword || hasMatchingChildren
  })
}

const loadGroupDetail = async (groupId: number) => {
  try {
    const group = await userApi.getUserGroup(groupId)
    selectedGroup.value = group
    await loadGroupUsers(groupId)
  } catch (error) {
    console.error('[organization] failed to load group detail:', error)
  }
}

const loadGroupUsers = async (groupId: number) => {
  try {
    usersLoading.value = true
    const users = await userApi.getGroupUsers(groupId)
    groupUsers.value = users
    usersPagination.total = users.length
  } catch (error) {
    console.error('[organization] failed to load group members:', error)
  } finally {
    usersLoading.value = false
  }
}

const onSelectNode = (selectedKeys: string[], info: any) => {
  // 选中节点的逻辑已在watch中处理
}

const onDrop = async (info: any) => {
  const dropKey = info.node.key
  const dragKey = info.dragNode.key
  const dropPos = info.node.pos.split('-')
  const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1])

  try {
    const dragGroupId = parseInt(dragKey)
    let newParentId: number | undefined
    
    // 判断是否拖拽到节点内部
    if (dropPosition === 0) {
      // 拖拽到节点内部，作为子节点
      newParentId = parseInt(dropKey)
    } else {
      // 拖拽到节点同级，获取目标节点的父节点
      const targetNode = findNodeInTree(treeData.value, dropKey)
      newParentId = targetNode?.parent_id
    }
    
    await userApi.moveUserGroup(dragGroupId, newParentId)
    message.success(t('organization.moveSuccess'))
    await loadTreeData()
  } catch (error) {
    console.error('[organization] drag & drop failed:', error)
  }
}

const onRightClick = (info: any) => {
  contextMenuNode.value = info.node
  contextMenuVisible.value = true
}

const handleContextMenu = ({ key }: { key: string }) => {
  contextMenuVisible.value = false
  
  switch (key) {
    case 'edit':
      editGroupFromContext()
      break
    case 'addChild':
      addChildGroup()
      break
    case 'delete':
      deleteGroupFromContext()
      break
  }
}

const showCreateModal = () => {
  isEditing.value = false
  resetForm()
  modalVisible.value = true
}

const editGroup = () => {
  if (!selectedGroup.value) return
  
  isEditing.value = true
  formData.name = selectedGroup.value.name
  formData.description = selectedGroup.value.description || ''
  formData.group_type = selectedGroup.value.group_type
  formData.parent_id = selectedGroup.value.parent_id
  formData.status = selectedGroup.value.status
  formData.platform_name = (selectedGroup.value as any).platform_name || 'CollabLingua'
  // 重置logo上传列表
  logoFileList.value = []
  logoFile.value = null
  modalVisible.value = true
}

const editGroupFromContext = () => {
  if (!contextMenuNode.value) return
  
  selectedKeys.value = [contextMenuNode.value.key]
  setTimeout(() => {
    editGroup()
  }, 100)
}

const addChildGroup = () => {
  if (!contextMenuNode.value) return
  
  isEditing.value = false
  resetForm()
  // 确保parent_id是有效的数字
  const parentId = parseInt(contextMenuNode.value.key)
  if (!isNaN(parentId) && parentId > 0) {
    formData.parent_id = parentId
  }
  modalVisible.value = true
}

const deleteGroup = async () => {
  if (!selectedGroup.value) return
  
  // 检查是否有子组
  const hasChildren = selectedGroup.value.children && selectedGroup.value.children.length > 0
  const hasUsers = selectedGroup.value.user_count && selectedGroup.value.user_count > 0
  
  const lines: string[] = [t('organization.deleteConfirmMessage', { name: selectedGroup.value.name })]

  if (hasChildren || hasUsers) {
    lines.push('', `⚠️ ${t('organization.deleteWarning')}`)
    if (hasChildren) {
      lines.push(t('organization.deleteSubgroups', { count: selectedGroup.value.children?.length || 0 }))
    }
    if (hasUsers) {
      lines.push(t('organization.deleteUsers', { count: selectedGroup.value.user_count || 0 }))
    }
    lines.push('', t('organization.deleteIrreversible'))
  } else {
    lines.push(t('organization.deleteIrreversibleSimple'))
  }

  const content = lines.join('\n')

  Modal.confirm({
    title: t('organization.deleteConfirmTitle'),
    content: content,
    okText: t('organization.confirmDelete'),
    cancelText: t('common.cancel'),
    okType: 'danger',
    onOk: async () => {
      try {
        await userApi.deleteUserGroup(selectedGroup.value!.id)
        message.success(t('organization.deleteSuccess'))
        selectedKeys.value = []
        selectedGroup.value = null
        await loadTreeData()
      } catch (error) {
        console.error('[organization] delete group failed:', error)
      }
    }
  })
}

const deleteGroupFromContext = () => {
  if (!contextMenuNode.value) return
  
  selectedKeys.value = [contextMenuNode.value.key]
  setTimeout(() => {
    deleteGroup()
  }, 100)
}

const handleSubmit = async () => {
  try {
    await formRef.value.validate()
    modalLoading.value = true
    
    // 处理platform_name：如果为空字符串，传None让数据库使用默认值
    let platform_name = formData.platform_name?.trim()
    if (!platform_name || platform_name === '') {
      platform_name = undefined  // 传undefined，让数据库使用默认值"CollabLingua"
    }
    
    const submitData = {
      name: formData.name.trim(), // 确保名称不为空
      description: formData.description?.trim() || '',
      group_type: formData.group_type,
      parent_id: formData.parent_id,
      platform_name: platform_name,
      ...(isEditing.value && { status: formData.status })
    }
    
    // 验证组名称
    if (!submitData.name || submitData.name.length === 0) {
      message.error(t('organization.groupNameNotEmpty'))
      return
    }
    
    let groupId: number
    
    if (isEditing.value && selectedGroup.value) {
      await userApi.updateUserGroup(selectedGroup.value.id, submitData)
      groupId = selectedGroup.value.id
      message.success(t('organization.updateSuccess'))
    } else {
      const newGroup = await userApi.createUserGroup(submitData)
      groupId = newGroup.id
      message.success(t('organization.createSuccess'))
    }
    
    // 如果有logo文件，上传logo
    // 注意：platform_name已经在创建/更新组时保存，通过创建/更新组API设置
    if (logoFile.value) {
      try {
        await userApi.uploadGroupLogo(groupId, logoFile.value)
        message.success(t('organization.logoUploadSuccess'))
      } catch (logoError: any) {
        console.error('[organization] upload logo failed:', logoError)
        message.warning(t('organization.logoUploadFailed'))
      }
    }
    
    modalVisible.value = false
    await loadTreeData()
    
    // 如果是编辑，重新加载详情
    if (isEditing.value && selectedGroup.value) {
      await loadGroupDetail(selectedGroup.value.id)
    }
    
    // 如果当前登录用户属于这个用户组，刷新用户信息以更新logo和平台名称
    const currentUserGroupId = (userStore.userInfo as any)?.group_id
    console.log('[organization] 检查是否需要刷新用户信息:', {
      currentUserGroupId,
      updatedGroupId: groupId,
      shouldRefresh: currentUserGroupId === groupId
    })
    
    if (currentUserGroupId === groupId) {
      try {
        // 刷新用户信息以更新logo和平台名称
        await userStore.getCurrentUser()
        // 等待响应式更新完成
        await nextTick()
        console.log('[organization] 用户信息已刷新:', {
          groupId: (userStore.userInfo as any)?.group?.id,
          logoPath: (userStore.userInfo as any)?.group?.logo_path,
          platformName: (userStore.userInfo as any)?.group?.platform_name
        })
      } catch (error) {
        console.error('[organization] 刷新用户信息失败:', error)
        // 不显示错误提示，因为这不是关键操作
      }
    }
  } catch (error) {
    console.error('[organization] submit failed:', error)
  } finally {
    modalLoading.value = false
  }
}

const handleCancel = () => {
  modalVisible.value = false
  resetForm()
}

const resetForm = () => {
  formData.name = ''
  formData.description = ''
  formData.group_type = 'normal'
  formData.parent_id = undefined
  formData.status = 'active'
  formData.platform_name = 'CollabLingua'  // 重置为默认值
  // 重置logo相关状态
  logoFileList.value = []
  logoFile.value = null
  formRef.value?.resetFields()
}

// Logo上传前处理
const beforeUploadLogo = (file: File) => {
  const isValidType = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/svg+xml', 'image/webp'].includes(file.type)
  const isLt2M = file.size / 1024 / 1024 < 2

  if (!isValidType) {
    message.error(t('organization.logoFormatError'))
    return false
  }
  if (!isLt2M) {
    message.error(t('organization.logoSizeError'))
    return false
  }

  // 保存文件，等待提交时上传
  logoFile.value = file
  logoFileList.value = [file]
  return false // 阻止自动上传
}

// 移除Logo
const handleRemoveLogo = () => {
  logoFileList.value = []
  logoFile.value = null
  return true
}


const refreshTree = () => {
  loadTreeData()
}

const onSearch = (value: string) => {
  // 搜索逻辑已在计算属性中处理
}


const removeFromGroup = async (user: UserInfo) => {
  Modal.confirm({
    title: t('organization.removeConfirmTitle'),
    content: t('organization.removeConfirmMessage', { name: user.name }),
    okText: t('common.confirm'),
    cancelText: t('common.cancel'),
    onOk: async () => {
      try {
        await userApi.updateUser(user.id, { group_id: undefined })
        message.success(t('organization.removeSuccess'))
        if (selectedGroup.value) {
          await loadGroupUsers(selectedGroup.value.id)
        }
      } catch (error) {
        console.error('[organization] remove user failed:', error)
      }
    }
  })
}

// 辅助方法
const getStatusText = (status: string) => {
  const statusMap: Record<string, string> = {
    active: t('organization.status.active'),
    inactive: t('organization.status.inactive'),
    suspended: t('organization.status.suspended')
  }
  return statusMap[status] || status
}

const getGroupTypeLabel = (groupType: string) => {
  return groupType === 'metalingual'
    ? t('organization.metalingualTag')
    : t('organization.normalGroupTag')
}

const getGroupTypeDetailLabel = (groupType: string) => {
  return groupType === 'metalingual'
    ? t('organization.metalingualGroup')
    : t('organization.normalGroup')
}

const formatDateTime = (dateTime: string) => {
  return new Date(dateTime).toLocaleString('zh-CN')
}

const findNodeInTree = (nodes: any[], key: string): any => {
  for (const node of nodes) {
    if (node.key === key) {
      return node
    }
    if (node.children && node.children.length > 0) {
      const found = findNodeInTree(node.children, key)
      if (found) return found
    }
  }
  return null
}
</script>

<style scoped>
.organization-management {
  padding: 24px;
}

.page-header {
  margin-bottom: 24px;
  
  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    
    h2 {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      flex-shrink: 0;
    }
    
    .header-actions {
      flex-shrink: 0;
    }
  }
}

.content-wrapper {
  min-height: calc(100vh - 200px);
}

.tree-card,
.detail-card {
  height: calc(100vh - 200px);
}

.tree-card :deep(.ant-card-body) {
  height: calc(100% - 57px);
  overflow: auto;
}

.detail-card :deep(.ant-card-body) {
  height: calc(100% - 57px);
  overflow: auto;
}

.tree-node-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.node-name {
  flex: 1;
  margin-right: 8px;
}

.node-tags {
  flex-shrink: 0;
}

.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
}

.group-detail {
  padding: 16px 0;
}

.quota-info {
  margin: 16px 0;
}

.no-quota {
  text-align: center;
  padding: 20px 0;
}

:deep(.ant-tree-node-content-wrapper) {
  width: calc(100% - 24px);
}

:deep(.ant-tree-title) {
  width: 100%;
}

:deep(.ant-descriptions-item-label) {
  width: 120px;
}

:deep(.ant-list-item-meta-title) {
  margin-bottom: 4px;
}

:deep(.ant-list-item-meta-description) {
  font-size: 12px;
  color: #666;
}

:deep(.ant-list-item-meta-description > div) {
  margin-bottom: 2px;
}
</style>